import type { OrgRole, Project } from '@outpost/api-client';
import {
  type UseMutationResult,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  Archive,
  ArchiveRestore,
  Eraser,
  FileClock,
  KeyRound,
  Plus,
  Trash2,
  Users,
  UsersRound,
} from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { CopyButton } from '@/components/copy-button';
import { SdkInstall } from '@/components/sdk-install';
import { ErrorState, ListSkeleton } from '@/components/states';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { useAuth } from '@/lib/auth';
import { allows, useOrgRole } from '@/lib/roles';
import { useTeams } from '@/lib/teams';

export function SettingsPage() {
  const { projectId = '' } = useParams();
  const { client } = useAuth();
  const queryClient = useQueryClient();

  const project = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => client.getProject(projectId),
  });
  const keys = useQuery({
    queryKey: ['keys', projectId],
    queryFn: () => client.listKeys(projectId),
  });
  const role = useOrgRole(project.data?.org_id);

  const createKey = useMutation({
    mutationFn: () => client.createKey(projectId),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['keys', projectId] }),
  });
  const revokeKey = useMutation({
    mutationFn: (keyId: string) => client.revokeKey(projectId, keyId),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['keys', projectId] }),
  });

  const dsn = keys.data?.find((key) => key.is_active)?.dsn ?? '';

  if (keys.isLoading) return <ListSkeleton rows={3} />;
  if (keys.isError) return <ErrorState error={keys.error} onRetry={() => void keys.refetch()} />;

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-semibold">{project.data?.name ?? 'Project'} settings</h1>
        <p className="text-sm text-muted-foreground">
          Client keys, the team that looks after this project, its members, how long source maps are
          kept and installation instructions.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <KeyRound className="size-4" />
            Client keys (DSN)
          </CardTitle>
          <CardDescription>
            A DSN authorizes writes to this project only. It is safe to ship in a browser bundle,
            and grants no ability to read your data.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {keys.data?.map((key) => (
            <div
              key={key.id}
              className="flex items-center gap-3 rounded-md border border-border px-3 py-2"
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium">{key.label}</span>
                  {!key.is_active && <Badge variant="muted">revoked</Badge>}
                </div>
                <code className="block truncate text-xs text-muted-foreground">{key.dsn}</code>
              </div>
              <CopyButton value={key.dsn} />
              {key.is_active && (
                <Button
                  variant="ghost"
                  size="icon"
                  title="Revoke this key"
                  onClick={() => revokeKey.mutate(key.id)}
                  disabled={revokeKey.isPending}
                >
                  <Trash2 />
                </Button>
              )}
            </div>
          ))}

          <Button variant="outline" size="sm" onClick={() => createKey.mutate()}>
            <Plus />
            Add a key
          </Button>
        </CardContent>
      </Card>

      {project.data && <ProjectTeam project={project.data} role={role} />}

      {project.data && allows(role, 'admin') && <ProjectMembers project={project.data} />}

      {project.data && (
        // Keyed on the stored value so the draft resets whenever it changes.
        <SourceMapRetention
          key={`${project.data.id}:${project.data.stale_release_days}`}
          project={project.data}
          role={role}
        />
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Install the SDK</CardTitle>
          <CardDescription>Copy the snippet for your platform to start reporting.</CardDescription>
        </CardHeader>
        <CardContent>
          <SdkInstall dsn={dsn} platforms={project.data?.platforms} />
        </CardContent>
      </Card>

      {project.data && allows(role, 'admin') && (
        <ArchiveOrDelete project={project.data} role={role} />
      )}
    </div>
  );
}

/**
 * Retiring a project, or clearing out what it has recorded. Archiving is
 * reversible and keeps everything readable, so admins may do it; purging issues
 * and deleting the project are neither, so they are owner-only and ask for the
 * slug to be typed.
 */
function ArchiveOrDelete({ project, role }: { project: Project; role: OrgRole | undefined }) {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [purged, setPurged] = useState<number | null>(null);
  const archived = !project.is_active;

  const setArchived = useMutation({
    mutationFn: (archive: boolean) =>
      archive ? client.archiveProject(project.id) : client.unarchiveProject(project.id),
    onSuccess: (updated) => {
      queryClient.setQueryData(['project', project.id], updated);
      void queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
  });
  const purge = useMutation({
    mutationFn: () => client.purgeIssues(project.id),
    onSuccess: (result) => {
      setPurged(result.issues);
      // The issue stream, its counts and sparklines are all keyed by the project.
      void queryClient.invalidateQueries({
        predicate: (query) => query.queryKey.includes(project.id),
      });
    },
  });
  const remove = useMutation({
    mutationFn: () => client.deleteProject(project.id),
    onSuccess: () => {
      navigate('/', { replace: true });
      // Anything cached for the project would now only ever answer 404.
      queryClient.removeQueries({ predicate: (query) => query.queryKey.includes(project.id) });
      void queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
  });

  return (
    <Card className="border-destructive/40">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Archive className="size-4" />
          Archive, purge or delete
        </CardTitle>
        <CardDescription>
          {archived
            ? 'This project is archived: its DSN keys are refused, so SDKs stop sending events. Everything it recorded is still readable.'
            : 'Archiving stops the project accepting events and keeps everything it has recorded. It can be undone at any time.'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            disabled={setArchived.isPending}
            onClick={() => {
              if (archived) {
                setArchived.mutate(false);
              } else if (
                window.confirm(`Archive ${project.name}? It will stop accepting events.`)
              ) {
                setArchived.mutate(true);
              }
            }}
          >
            {archived ? <ArchiveRestore /> : <Archive />}
            {archived ? 'Unarchive project' : 'Archive project'}
          </Button>

          {allows(role, 'owner') && (
            <>
              <ConfirmBySlug
                project={project}
                trigger={
                  <>
                    <Eraser />
                    Purge issues
                  </>
                }
                title={`Purge every issue in ${project.name}?`}
                description={
                  <>
                    Every issue in this project is deleted permanently, together with its events,
                    comments and history. Releases, source maps, DSN keys and alert rules are kept,
                    and the project goes on accepting events, which start new issues. This cannot be
                    undone.
                  </>
                }
                action="Purge issues permanently"
                inputId="confirm-purge"
                mutation={purge}
                onOpen={() => setPurged(null)}
              />

              <ConfirmBySlug
                project={project}
                trigger={
                  <>
                    <Trash2 />
                    Delete project
                  </>
                }
                title={`Delete ${project.name}?`}
                description={
                  <>
                    Every issue, event, release, alert rule and DSN key in this project is deleted
                    permanently, and SDKs still using its DSN have their events refused. This cannot
                    be undone.
                    {!archived && ' To keep the data, archive the project instead.'}
                  </>
                }
                action="Delete permanently"
                inputId="confirm-delete"
                mutation={remove}
              />
            </>
          )}
        </div>

        {purged !== null && (
          <p className="text-sm text-muted-foreground">
            Purged {purged} {purged === 1 ? 'issue' : 'issues'}.
          </p>
        )}
        {setArchived.error && (
          <p className="text-sm text-destructive">{(setArchived.error as Error).message}</p>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * A destructive action that only goes ahead once the project's slug has been
 * typed, so it cannot be done to the wrong project by a stray click.
 */
function ConfirmBySlug({
  project,
  trigger,
  title,
  description,
  action,
  inputId,
  mutation,
  onOpen,
}: {
  project: Project;
  trigger: ReactNode;
  title: string;
  description: ReactNode;
  action: string;
  inputId: string;
  mutation: UseMutationResult<unknown, Error, void>;
  onOpen?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [confirmation, setConfirmation] = useState('');

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (next) {
          onOpen?.();
        } else {
          setConfirmation('');
          mutation.reset();
        }
      }}
    >
      <DialogTrigger asChild>
        <Button variant="destructive">{trigger}</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5">
          <Label htmlFor={inputId}>
            Type <code>{project.slug}</code> to confirm
          </Label>
          <Input
            id={inputId}
            autoComplete="off"
            value={confirmation}
            onChange={(event) => setConfirmation(event.target.value)}
          />
        </div>
        {mutation.error && <p className="text-sm text-destructive">{mutation.error.message}</p>}
        <DialogFooter>
          <Button
            variant="destructive"
            onClick={() =>
              mutation.mutate(undefined, {
                onSuccess: () => {
                  setOpen(false);
                  setConfirmation('');
                },
              })
            }
            disabled={confirmation !== project.slug || mutation.isPending}
          >
            {action}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** What a project starts with, and what switching the sweep back on offers. */
const DEFAULT_STALE_RELEASE_DAYS = 90;
/** The server's `releases::KEPT_RELEASES`. */
const KEPT_RELEASES = 5;
const MAX_STALE_RELEASE_DAYS = 3650;

/**
 * How long a release's source maps outlive its last event or upload.
 *
 * Stored events are never reprocessed, so maps only serve events still to come
 * from a release; without a limit, CI that uploads per commit grows storage
 * forever. Shown to everyone who can see the project, because it explains why
 * an old release has no maps, but only an admin can change it.
 */
function SourceMapRetention({ project, role }: { project: Project; role: OrgRole | undefined }) {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  const canEdit = allows(role, 'admin');
  const stored = project.stale_release_days;
  const [draft, setDraft] = useState(String(stored ?? DEFAULT_STALE_RELEASE_DAYS));

  const save = useMutation({
    mutationFn: (days: number | null) => client.setStaleReleaseDays(project.id, days),
    onSuccess: (updated) => {
      queryClient.setQueryData(['project', project.id], updated);
      void queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
  });

  // Where the switch is going while the change is in flight, rather than
  // snapping back until the response arrives.
  const sweeping = save.isPending ? save.variables !== null : stored !== null;
  const days = Number(draft);
  const valid = Number.isInteger(days) && days >= 1 && days <= MAX_STALE_RELEASE_DAYS;
  const changed = valid && days !== stored;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <FileClock className="size-4" />
          Source maps
        </CardTitle>
        <CardDescription>
          A release&apos;s source maps are only used for events still to come from it: events
          already stored are never reprocessed. Deleting them once a release has gone quiet keeps
          storage from growing with every upload. The newest {KEPT_RELEASES} releases with source
          maps are always kept, however quiet.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {canEdit ? (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
            <Switch
              id="stale-release-sweep"
              checked={sweeping}
              onCheckedChange={(checked) =>
                save.mutate(checked ? (valid ? days : DEFAULT_STALE_RELEASE_DAYS) : null)
              }
              disabled={save.isPending}
              aria-label="Delete source maps of quiet releases"
            />
            {sweeping ? (
              <>
                <Label htmlFor="stale-release-days" className="font-normal">
                  Delete source maps for releases with no activity for
                </Label>
                <Input
                  id="stale-release-days"
                  type="number"
                  inputMode="numeric"
                  min={1}
                  max={MAX_STALE_RELEASE_DAYS}
                  className="h-8 w-24"
                  value={draft}
                  onChange={(event) => setDraft(event.target.value)}
                  aria-invalid={!valid}
                />
                <span className="text-sm">days</span>
                {changed && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => save.mutate(days)}
                    disabled={save.isPending}
                  >
                    Save
                  </Button>
                )}
              </>
            ) : (
              <Label htmlFor="stale-release-sweep" className="font-normal">
                Keep source maps forever
              </Label>
            )}
          </div>
        ) : (
          <p className="text-sm">
            {stored === null
              ? 'Source maps are kept forever.'
              : `Source maps are deleted for releases with no activity for ${stored} ${stored === 1 ? 'day' : 'days'}.`}{' '}
            <span className="text-muted-foreground">An admin decides how long.</span>
          </p>
        )}

        {canEdit && sweeping && !valid && (
          <p className="text-sm text-destructive">
            Enter a whole number of days from 1 to {MAX_STALE_RELEASE_DAYS}.
          </p>
        )}
        {save.error && <p className="text-sm text-destructive">{(save.error as Error).message}</p>}
      </CardContent>
    </Card>
  );
}

/** Radix needs a value for every option, and the empty string is not one. */
const NO_TEAM = 'none';

/**
 * Which team looks after this project.
 *
 * A project belongs to at most one, and everyone on that team can open it.
 * Shown to everyone who can see the project, because it answers "why can I see
 * this?" — but only an admin can change it.
 */
function ProjectTeam({ project, role }: { project: Project; role: OrgRole | undefined }) {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  const teams = useTeams(project.org_id);
  const canEdit = allows(role, 'admin');

  const assign = useMutation({
    mutationFn: (teamId: string) =>
      client.setProjectTeam(project.id, teamId === NO_TEAM ? null : teamId),
    onSuccess: (updated) => {
      queryClient.setQueryData(['project', project.id], updated);
      void queryClient.invalidateQueries({ queryKey: ['projects'] });
      void queryClient.invalidateQueries({ queryKey: ['teams', project.org_id] });
      // Who can see the project has just changed.
      void queryClient.invalidateQueries({ queryKey: ['project-members', project.id] });
    },
  });

  const current = teams.data?.find((team) => team.id === project.team_id);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <UsersRound className="size-4" />
          Team
        </CardTitle>
        <CardDescription>
          {canEdit
            ? "Everyone on this project's team can open it. Moving it to another team hands it over: the old team loses it, along with the access it gave."
            : "Everyone on this project's team can open it. An admin decides which team that is."}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {canEdit ? (
          <Select
            value={project.team_id ?? NO_TEAM}
            onValueChange={(value) => assign.mutate(value)}
            disabled={assign.isPending || teams.isLoading}
          >
            <SelectTrigger className="w-full sm:w-72">
              <SelectValue placeholder="No team" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NO_TEAM}>No team</SelectItem>
              {teams.data?.map((team) => (
                <SelectItem key={team.id} value={team.id}>
                  {team.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          !current && (
            <p className="text-sm text-muted-foreground">This project does not belong to a team.</p>
          )
        )}

        {/* Named once, as a way through to the team, for everyone who can see it. */}
        {current && (
          <p className="text-sm text-muted-foreground">
            <Link to={`/teams/${current.id}`} className="underline underline-offset-4">
              {current.name}
            </Link>{' '}
            has {current.member_count} {current.member_count === 1 ? 'member' : 'members'}, and all
            of them can see this project.
          </p>
        )}
        {canEdit && teams.data?.length === 0 && (
          <p className="text-sm text-muted-foreground">
            This organization has no teams yet. Create one in its settings to give a group of people
            several projects at once.
          </p>
        )}

        {assign.error && (
          <p className="text-sm text-destructive">{(assign.error as Error).message}</p>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Who can see this project. Only rendered for admins, who are the only ones
 * the server lets change it.
 *
 * Only organization members with the `member` role are offered for
 * assignment: admins and owners already see every project, so assigning them
 * would change nothing. The people the project's team brings with it are listed
 * separately and cannot be removed here — that is the team's page.
 */
function ProjectMembers({ project }: { project: Project }) {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState('');
  const projectId = project.id;

  const members = useQuery({
    queryKey: ['project-members', projectId],
    queryFn: () => client.listProjectMembers(projectId),
  });
  const orgMembers = useQuery({
    queryKey: ['org-members', project.org_id],
    queryFn: () => client.listMembers(project.org_id),
  });

  const invalidate = () =>
    void queryClient.invalidateQueries({ queryKey: ['project-members', projectId] });
  const add = useMutation({
    mutationFn: (userId: string) => client.addProjectMember(projectId, userId),
    onSuccess: () => {
      setSelected('');
      invalidate();
    },
  });
  const remove = useMutation({
    mutationFn: (userId: string) => client.removeProjectMember(projectId, userId),
    onSuccess: invalidate,
  });

  const assigned = members.data?.filter((member) => member.assigned) ?? [];
  const throughTeam = members.data?.filter((member) => member.via_team && !member.assigned) ?? [];
  const assignable =
    orgMembers.data?.filter(
      (candidate) =>
        candidate.role === 'member' && !members.data?.some((member) => member.id === candidate.id),
    ) ?? [];
  const error = add.error ?? remove.error;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Users className="size-4" />
          Members
        </CardTitle>
        <CardDescription>
          People with the member role only see the projects they are added to, here or through a
          team. Admins and owners can access every project in the organization.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {members.isError && (
          <ErrorState error={members.error} onRetry={() => void members.refetch()} />
        )}

        {members.data && assigned.length === 0 && throughTeam.length === 0 && (
          <p className="text-sm text-muted-foreground">No members have been added yet.</p>
        )}

        {assigned.map((member) => (
          <div
            key={member.id}
            className="flex items-center gap-3 rounded-md border border-border px-3 py-2"
          >
            <div className="min-w-0 flex-1">
              <span className="block text-sm font-medium">{member.name}</span>
              <span className="block truncate text-xs text-muted-foreground">{member.email}</span>
            </div>
            <Button
              variant="ghost"
              size="icon"
              title={`Remove ${member.name} from this project`}
              onClick={() => remove.mutate(member.id)}
              disabled={remove.isPending}
            >
              <Trash2 />
            </Button>
          </div>
        ))}

        {assignable.length > 0 && (
          <div className="flex items-center gap-2">
            <Select value={selected} onValueChange={setSelected}>
              <SelectTrigger className="w-full sm:w-72">
                <SelectValue placeholder="Choose a member" />
              </SelectTrigger>
              <SelectContent>
                {assignable.map((candidate) => (
                  <SelectItem key={candidate.id} value={candidate.id}>
                    {candidate.name} ({candidate.email})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              size="sm"
              onClick={() => add.mutate(selected)}
              disabled={!selected || add.isPending}
            >
              <Plus />
              Add
            </Button>
          </div>
        )}

        {orgMembers.data && assignable.length === 0 && (
          <p className="text-xs text-muted-foreground">
            Everyone with the member role already has access.
          </p>
        )}

        {throughTeam.length > 0 && (
          <div className="space-y-2 rounded-md border border-dashed border-border px-3 py-3">
            <p className="text-xs text-muted-foreground">
              Through this project&apos;s{' '}
              {project.team_id ? (
                <Link to={`/teams/${project.team_id}`} className="underline underline-offset-4">
                  team
                </Link>
              ) : (
                'team'
              )}
              . Take someone off the team to take this away.
            </p>
            {throughTeam.map((member) => (
              <div key={member.id} className="flex items-center gap-3 text-sm">
                <span className="min-w-0 flex-1 truncate">
                  {member.name}{' '}
                  <span className="text-xs text-muted-foreground">{member.email}</span>
                </span>
                <Badge variant="muted">team</Badge>
              </div>
            ))}
          </div>
        )}

        {error && <p className="text-sm text-destructive">{(error as Error).message}</p>}
      </CardContent>
    </Card>
  );
}
